'use client';

import { useEffect, useState, useContext, type FC } from 'react';
import {
  Alert,
  Button,
  ButtonGroup,
  Dialog,
  Flex,
  Textarea,
  Input,
  Separator,
  Heading,
  Pill,
  Text,
  Tooltip,
} from '@/_components';
import { saveQuestionnaires, sendReminders } from '@/_api/admin-questionnaires';
import { ToastContext } from '@/_contexts/ToastProvider';
import { UserContext } from '@/_contexts/UserProvider';
import type { EditTemplateReminderDialogProps } from './EditTemplateReminderDialog.types';
import { Questionnaire } from '@/_types/models/questionnaire';
import { QuestionnaireContext } from '@/_contexts/admin/QuestionnaireProvider';
import { QUESTIONNAIRE_REMINDER_DEFAULT_SETTINGS } from '@/_constants/questionnaires';

export const EditTemplateReminderDialog: FC<
  EditTemplateReminderDialogProps
> = ({
  isOpen,
  onClose,
  selectedQuestionnaires,
  title,
  reminderEnabledQuestionnairesCount,
}) => {
  const { addToast } = useContext(ToastContext);
  const { token } = useContext(UserContext);
  const { loadQuestionnaires } = useContext(QuestionnaireContext);

  const [isLoading, setIsLoading] = useState(false);

  const questionnaireIds: Array<number> = selectedQuestionnaires
    .filter((q) => !q.locked && !q.completed)
    .map((q) => q.id);

  const getUniqueValue = (array: any[]) => {
    const uniqueSet = new Set(array);
    return uniqueSet.size === 1 ? [...uniqueSet][0] : undefined;
  };

  const uniqueEmailSubject = getUniqueValue(
    selectedQuestionnaires.map((q) => q.reminder_email_subject),
  );
  const uniqueEmailMessage = getUniqueValue(
    selectedQuestionnaires.map((q) => q.reminder_email_message),
  );
  const uniqueInAppMessage = getUniqueValue(
    selectedQuestionnaires.map((q) => q.reminder_in_app_message),
  );
  const uniqueSmsMessage = getUniqueValue(
    selectedQuestionnaires.map((q) => q.reminder_sms_message),
  );

  const [subject, setSubject] = useState<string>('');
  const [emailMessage, setEmailMessage] = useState<string>('');
  const [appMessage, setAppMessage] = useState<string>('');
  const [smsMessage, setSmsMessage] = useState<string>('');

  const baseParams = {
    questionnaire_ids: questionnaireIds,
    reminder_email_subject: subject,
    reminder_email_message: emailMessage,
    reminder_in_app_message: appMessage,
    reminder_sms_message: smsMessage,
    customize: true,
  };

  // when unmounted, reset all state:
  useEffect(() => {
    return () => {
      setIsLoading(false);
    };
  }, []);

  useEffect(() => {
    let message = QUESTIONNAIRE_REMINDER_DEFAULT_SETTINGS.emailMessage;
    if (uniqueEmailMessage) message = uniqueEmailMessage;
    setEmailMessage(message);
  }, [uniqueEmailMessage]);

  useEffect(() => {
    let subject = QUESTIONNAIRE_REMINDER_DEFAULT_SETTINGS.emailSubject;
    if (uniqueEmailSubject) subject = uniqueEmailSubject;
    setSubject(subject);
  }, [uniqueEmailSubject]);

  useEffect(() => {
    let message = QUESTIONNAIRE_REMINDER_DEFAULT_SETTINGS.inAppMessage;
    if (uniqueInAppMessage) message = uniqueInAppMessage;
    setAppMessage(message);
  }, [uniqueInAppMessage]);

  useEffect(() => {
    let message = QUESTIONNAIRE_REMINDER_DEFAULT_SETTINGS.smsMessage;
    if (uniqueSmsMessage) message = uniqueSmsMessage;
    setSmsMessage(message);
  }, [uniqueSmsMessage]);

  const onSubjectChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setSubject(event.target.value);
  const onEmailMessageChange = (
    event: React.ChangeEvent<HTMLTextAreaElement>,
  ) => setEmailMessage(event.target.value);
  const onAppMessageChange = (event: React.ChangeEvent<HTMLTextAreaElement>) =>
    setAppMessage(event.target.value);
  const onSmsMessageChange = (event: React.ChangeEvent<HTMLTextAreaElement>) =>
    setSmsMessage(event.target.value);

  const onSendNow = async () => {
    try {
      setIsLoading(true);

      const response = await sendReminders({
        params: baseParams,
        token,
      });

      if (response.error) {
        addToast('error', response.error);
        return;
      }

      addToast('success', 'Reminders sent successfully');
    } catch (error) {
      console.error(error);
      addToast('error', null, { error });
    } finally {
      setIsLoading(false);
      onClose();
    }
  };

  const onSave = async () => {
    try {
      setIsLoading(true);

      const response = await saveQuestionnaires({
        questionnaire_ids: questionnaireIds,
        reminder_email_subject: subject,
        reminder_email_message: emailMessage,
        reminder_in_app_message: appMessage,
        reminder_sms_message: smsMessage,
        customize: true,
        token,
      });

      if (!response.error) {
        addToast('success', 'Reminder updated');
        loadQuestionnaires();
      }
    } catch (error) {
      addToast('error', 'Reminder update failed', { error });
    } finally {
      setIsLoading(false);
      onClose();
    }
  };

  const lastReminderSent = selectedQuestionnaires.reduce(
    (
      acc: { date: Date | undefined; count: number },
      questionnaire: Questionnaire,
    ) => {
      // Example of last_followed_up: "2024-06-28T11:47:46.523-07:00"
      if (questionnaire.last_followed_up) {
        const lastFollowedUpDate = new Date(questionnaire.last_followed_up);

        if (!acc.date || lastFollowedUpDate > acc.date) {
          return { date: lastFollowedUpDate, count: acc.count + 1 };
        } else {
          return { date: acc.date, count: acc.count + 1 };
        }
      }

      return acc;
    },
    { date: undefined, count: 0 },
  );

  const reminderDisabledQuestionnariesCount = reminderEnabledQuestionnairesCount
    ? selectedQuestionnaires.length - reminderEnabledQuestionnairesCount
    : 0;

  const allReminderMatched: boolean =
    !!uniqueEmailSubject &&
    !!uniqueEmailMessage &&
    !!uniqueInAppMessage &&
    !!uniqueSmsMessage;

  return (
    <Dialog isOpen={isOpen} onClose={onClose} title={title}>
      <Dialog.Content>
        <Flex isColumn={true} gap={6}>
          {lastReminderSent.date && (
            <Alert type="info" icon="info">
              {`Are you sure you want to send reminder for the selected questionnaire? The last reminder was sent on ${lastReminderSent.date.toLocaleDateString()} to ${lastReminderSent.count} questionnaire${lastReminderSent.count > 1 ? 's' : ''}.`}
            </Alert>
          )}
          {reminderDisabledQuestionnariesCount > 0 && (
            <Alert type="warning" icon="warning">
              Reminders will not be sent to{' '}
              {reminderDisabledQuestionnariesCount} questionnaires that{' '}
              {reminderDisabledQuestionnariesCount > 1 ? 'are' : 'is'} locked,
              completed, draft or have reminder disabled.
            </Alert>
          )}
          {!allReminderMatched && (
            <Alert type="info" icon="info">
              When the selected questionnaires have inconsistent reminder texts,
              default texts will be applied.
            </Alert>
          )}

          <>
            <Flex gap={2} isYCentered={true}>
              <Heading level={4}>Email</Heading>
              {(!uniqueEmailSubject || !uniqueEmailMessage) && (
                <Pill intent="info" label="Default" size="small" />
              )}
            </Flex>
            <Input value={subject} label="Subject" onChange={onSubjectChange} />
            <Textarea
              rows={10}
              value={emailMessage}
              label="Body"
              onChange={onEmailMessageChange}
            />
            <Separator />

            <Flex gap={2} isYCentered={true}>
              <Heading level={4}>In-App Notification</Heading>
              {!uniqueInAppMessage && (
                <Pill intent="info" label="Default" size="small" />
              )}
            </Flex>
            <Textarea
              rows={3}
              value={appMessage}
              label="In App Notification"
              isLabelHidden={true}
              onChange={onAppMessageChange}
            />
            <Separator />

            <Flex gap={2} isYCentered={true}>
              <Heading level={4}>SMS Notification</Heading>
              <Tooltip>
                <Tooltip.Content>
                  <Flex>
                    <Text type="tiny" color="currentcolor">
                      If the user has a phone number and opt in
                    </Text>
                  </Flex>
                </Tooltip.Content>
                <Tooltip.Trigger>
                  <Pill
                    size="small"
                    iconLeft="info"
                    label=""
                    isIconOnly={true}
                    intent="info"
                  />
                </Tooltip.Trigger>
              </Tooltip>
              {!uniqueSmsMessage && (
                <Pill intent="info" label="Default" size="small" />
              )}
            </Flex>
            <Textarea
              rows={3}
              value={smsMessage}
              label="SMS Notification"
              isLabelHidden={true}
              onChange={onSmsMessageChange}
            />
          </>

          <ButtonGroup align="right" isSameLine>
            <Button
              intent="danger-secondary"
              isDisabled={isLoading}
              onClick={onClose}
              value="Cancel"
            />
            <Button
              intent="secondary"
              isDisabled={isLoading}
              onClick={onSave}
              value="Save & Do Not Send"
            />
            <Button
              intent="primary"
              isDisabled={isLoading}
              onClick={onSendNow}
              value="Send Now"
            />
          </ButtonGroup>
        </Flex>
      </Dialog.Content>
    </Dialog>
  );
};
